/**
 * Поля ответа ученика, которые службы переименовывают (договор docs/CONTRACTS/student-api.md §4,
 * техдолг 258, решение владельца 21.09.2026).
 *
 * ⚠️ ФРОНТ ПЕРВЫМ. Пара идёт так: фронт читает оба имени → фронт на проде → служба отдаёт новое имя.
 * Наоборот нельзя: служба с новым именем при старом фронте молча отнимет у ученика разделы, чат,
 * оценки и отзывы.
 *
 * Правило у всех одно: новое имя пришло (не `undefined`/`null`) — побеждает оно; нет — старое;
 * нет ни того ни другого — то же значение по умолчанию, что было до переименования.
 *
 * ⚠️ ЧИТАТЬ ЭТИ ПОЛЯ ТОЛЬКО ОТСЮДА. Прямое чтение в экране ученика ловит
 * `studentFieldsSingleReader.test.ts`: разбросанное `a ?? b` забудут поменять местами при заходе 2.
 */

const isObject = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v);

/**
 * Разделы ученика: `sections`, раньше `features` (`/auth/me`, ответ входа).
 * Нет ни одного — `undefined`: на сайте `computeHasFeature` показывает всё (безопасно к блокировке), в
 * приложении — только Главная и Профиль (D75, `lib/storeTabs.ts`).
 */
export function userSections(user: { sections?: string[] | null; features?: string[] | null }): string[] | undefined {
  return user.sections ?? user.features ?? undefined;
}

/**
 * Новый объект пользователя без набора разделов не стирает прежний набор того же человека (26.09.2026).
 *
 * `setUser` зовут не только с `/auth/me`: смена языка (`PUT /auth/me`) и аватара тоже кладут ответ целиком.
 * Сервер с 26.09 отдаёт в них набор, но ответ без него (старая служба, сбой сборки набора) в приложении
 * сворачивал бы меню до Главной и Профиля до следующего `/auth/me` — у ученика «пропадали» бы разделы от
 * переключения языка. Другой человек (иной `id`) — набор не переносится.
 */
export function keepSections<T extends { id?: string; sections?: string[] | null; features?: string[] | null }>(
  prev: T | null,
  next: T,
): T {
  if (!prev || prev.id !== next.id) return next;
  if (userSections(next) !== undefined || userSections(prev) === undefined) return next;
  return { ...next, sections: prev.sections, features: prev.features };
}

/**
 * Открытый курс (виден каждому вошедшему и гостю без записи): `is_open`, раньше `is_free`.
 * Нет ни одного — не открытый.
 */
export function isOpenCourse(m: { is_open?: boolean | null; is_free?: boolean | null }): boolean {
  return (m.is_open ?? m.is_free) === true;
}

/**
 * Преподаватель в диалоге ученика: `teacher_id`, раньше `curator_id`.
 * Нет ни одного — `undefined`: нет куратора — нет и входа в чат (D49).
 */
export function teacherIdOf(
  session: { teacher_id?: string | null; curator_id?: string | null } | undefined,
): string | undefined {
  return session?.teacher_id || session?.curator_id || undefined;
}

/**
 * Имя преподавателя в диалоге ученика: `teacher_name`, раньше `curator_name` (заход «1б», план захода 2 §3 шаг 4).
 * Нет ни одного — `undefined`; подпись по умолчанию («Куратор») ставит экран, как и до переименования.
 */
export function teacherNameOf(
  session: { teacher_name?: string | null; curator_name?: string | null } | undefined,
): string | undefined {
  return session?.teacher_name ?? session?.curator_name ?? undefined;
}

/**
 * Фото преподавателя в диалоге ученика: `teacher_avatar_url`, раньше `curator_avatar_url`.
 * Нет ни одного — `undefined`, и экран рисует буквы: это нормальное состояние.
 */
export function teacherAvatarOf(
  session: { teacher_avatar_url?: string | null; curator_avatar_url?: string | null } | undefined,
): string | undefined {
  return session?.teacher_avatar_url ?? session?.curator_avatar_url ?? undefined;
}

/**
 * Сырая оценка урока из `lessons[]` в `my-progress`: `grade`, раньше `curator_grade`. Это объект
 * `{grade, feedback, updated_at}`, разбирает его `parseCuratorGrade`.
 *
 * ⚠️ `grade` БЕРЁТСЯ ТОЛЬКО ОБЪЕКТОМ. Под тем же именем в других ответах лежит число (кураторская
 * строка урока, старая оценка домашки, D15). Число — не новое имя, а другое поле: тогда смотрим
 * `curator_grade`. И `grade: null` не глушит живой `curator_grade`.
 */
export function lessonGradeRaw(row: Record<string, unknown>): unknown {
  return isObject(row.grade) ? row.grade : row.curator_grade;
}

/** Отзыв к домашке: `review_note`, раньше `curator_note`. Нет ни одного — пустая строка. */
export function reviewNoteOf(sub: { review_note?: string | null; curator_note?: string | null }): string {
  return sub.review_note ?? sub.curator_note ?? '';
}
